/**
 * ProduktionPage — Google Drive file matching and move for "Druck" orders.
 *
 * Flow:
 *   Step 1 (select)  — checkbox list of "Druck" orders; pick which to process
 *   Step 2 (match)   — scraper matches buyer names to Drive PNGs; admin resolves
 *                       ambiguities via dropdowns; per-order checkboxes control
 *                       which orders are actually moved (default: only clean matches)
 *   Step 3 (done)    — results: which files were moved, which failed
 */
import React, { useEffect, useState, useRef } from "react";
import {
  collection, query, where, getDocs, doc, getDoc, addDoc,
  updateDoc, deleteDoc, onSnapshot, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import GDriveSettings from "./GDriveSettings";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}

function truncate(str, n = 60) {
  if (!str) return "";
  return str.length > n ? str.slice(0, n) + "…" : str;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  padding: "1.5em 2em",
  marginBottom: "1.5em",
};

const btnPrimary = {
  background: "#553c9a", color: "#fff",
  border: "none", borderRadius: "8px",
  padding: "0.6em 1.4em", cursor: "pointer",
  fontWeight: "600", fontSize: "0.9rem",
};

const btnSecondary = {
  background: "transparent", color: "#553c9a",
  border: "1px solid #553c9a", borderRadius: "8px",
  padding: "0.6em 1.4em", cursor: "pointer",
  fontWeight: "600", fontSize: "0.9rem",
};

const th = {
  textAlign: "left", fontSize: "0.75rem", fontWeight: "600",
  color: "#718096", padding: "0.5em 0.75em",
  borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
};

const td = {
  fontSize: "0.83rem", padding: "0.5em 0.75em",
  borderBottom: "1px solid #f0f4f8", color: "#2d3748",
  verticalAlign: "middle",
};

const confBadge = (conf) => ({
  display: "inline-block",
  padding: "0.15em 0.55em",
  borderRadius: "999px",
  fontSize: "0.72rem",
  fontWeight: "600",
  background: conf === "high"   ? "#d1fae5"
            : conf === "medium" ? "#fef3c7"
            :                     "#fee2e2",
  color:      conf === "high"   ? "#065f46"
            : conf === "medium" ? "#92400e"
            :                     "#991b1b",
});

// ── Main component ────────────────────────────────────────────────────────────

export default function ProduktionPage() {
  const [step,         setStep]         = useState("select");
  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(new Set());
  const [searchQuery,  setSearchQuery]  = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [stageFilter,  setStageFilter]  = useState("offen");

  // Step 2 state
  const [jobId,      setJobId]      = useState(null);
  const [jobData,    setJobData]    = useState(null);
  const [selections, setSelections] = useState({}); // {`${oid}_${idx}`: fileId}
  const [toMove,     setToMove]     = useState(new Set()); // orderIds checked for move
  const [confirming, setConfirming] = useState(false);

  const unsubRef       = useRef(null);
  const initializedRef = useRef(false); // guard: only auto-init toMove once

  useEffect(() => {
    loadOrders();
    return () => unsubRef.current?.();
  }, []);

  async function loadOrders() {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "etsy_orders"), where("workflowStage", "==", "druck"))
      );
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.shipByDate || "").localeCompare(b.shipByDate || ""));
      setOrders(list);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 1 selection ────────────────────────────────────────────────────────

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll(visible) {
    const ids = visible.map((o) => o.id);
    const allOn = ids.every((id) => selected.has(id));
    setSelected(allOn ? new Set() : new Set(ids));
  }

  const filteredOrders = orders.filter((o) => {
    if (stageFilter === "offen"      && o.druckStatus === "verschoben") return false;
    if (stageFilter === "verschoben" && o.druckStatus !== "verschoben") return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      o.buyerName?.toLowerCase().includes(q) ||
      o.id?.toLowerCase().includes(q) ||
      o.items?.some((i) => i.title?.toLowerCase().includes(q))
    );
  });

  // ── Step 2 helpers ──────────────────────────────────────────────────────────

  /** Is every item for this order resolved (a file selected)? */
  function orderResolved(orderId, matchItems, selMap) {
    return (matchItems || []).every((m, idx) => {
      const key = `${orderId}_${idx}`;
      return selMap[key] || m.selectedId;
    });
  }

  /** Are all checked (toMove) orders fully resolved? */
  function allToMoveResolved(matchesMap, selMap, toMoveSet) {
    if (toMoveSet.size === 0) return false;
    return Array.from(toMoveSet).every((oid) =>
      orderResolved(oid, matchesMap[oid], selMap)
    );
  }

  // ── Step 1 → Step 2 ────────────────────────────────────────────────────────

  async function proceedToMatch() {
    if (selected.size === 0) return;

    const settingsSnap = await getDoc(doc(db, "config", "gdriveSettings"));
    const settings = settingsSnap.exists()
      ? settingsSnap.data()
      : {
          sourceFolderId:  "1ZptFWJMIfM7qRipLqAwTqZCn4QzanpnV",
          destFolderId:    "14xBRflVYjy66CjbCeKq62XDXjKfLQ5Uz",
          localCopy:       false,
          localPath:       "C:\\Bilddateien_Printsonalities",
        };

    const docRef = await addDoc(collection(db, "production_queue"), {
      orderIds:  Array.from(selected),
      status:    "matching",
      matches:   {},
      settings,
      createdAt: serverTimestamp(),
    });

    setJobId(docRef.id);
    setSelections({});
    setToMove(new Set());
    initializedRef.current = false;
    setStep("match");

    unsubRef.current?.();
    unsubRef.current = onSnapshot(docRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setJobData(data);

      if (data.status === "matched" && !initializedRef.current) {
        initializedRef.current = true;

        // Build selections map from single candidates
        const newSel = {};
        Object.entries(data.matches || {}).forEach(([oid, items]) => {
          items.forEach((m, idx) => {
            if (m.candidates?.length === 1) {
              newSel[`${oid}_${idx}`] = m.candidates[0].fileId;
            }
          });
        });
        setSelections(newSel);

        // Default toMove = orders where ALL items have exactly 1 candidate (clean)
        const cleanOrders = new Set(
          Object.entries(data.matches || {})
            .filter(([, items]) =>
              items.every((m) => m.candidates?.length === 1)
            )
            .map(([oid]) => oid)
        );
        setToMove(cleanOrders);
      }
    });
  }

  // ── Step 2 → Step 3 ────────────────────────────────────────────────────────

  async function confirmMove() {
    if (!jobId || confirming || toMove.size === 0) return;
    setConfirming(true);

    // Build matches with selectedIds filled in; only pass checked orderIds
    const matches = { ...(jobData?.matches || {}) };
    Object.entries(matches).forEach(([oid, items]) => {
      matches[oid] = items.map((m, idx) => {
        const selArr = selections[`${oid}_${idx}`];
        const selectedIds = Array.isArray(selArr) && selArr.length
          ? selArr
          : m.selectedId ? [m.selectedId] : [];
        return { ...m, selectedIds };
      });
    });

    await updateDoc(doc(db, "production_queue", jobId), {
      status:   "confirmed",
      orderIds: Array.from(toMove), // only move checked orders
      matches,
    });
    setConfirming(false);
  }

  // ── Cancel ──────────────────────────────────────────────────────────────────

  async function cancelJob() {
    unsubRef.current?.();
    if (jobId) {
      try { await deleteDoc(doc(db, "production_queue", jobId)); } catch (_) {}
    }
    setJobId(null);
    setJobData(null);
    setStep("select");
  }

  async function finishDone() {
    unsubRef.current?.();
    setJobId(null);
    setJobData(null);
    setStep("select");
    await loadOrders();
  }

  const selectedOrders = orders.filter((o) => selected.has(o.id));

  // ── Step 1 ──────────────────────────────────────────────────────────────────

  if (step === "select") {
    return (
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        {showSettings && <GDriveSettings onClose={() => setShowSettings(false)} />}

        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: "1.5em" }}>
          <h2 style={{ fontWeight: "700", fontSize: "1.3rem", color: "#1a202c", margin: 0 }}>
            Produktion
          </h2>
          <div style={{ display: "flex", gap: "0.75em", alignItems: "center" }}>
            <button
              onClick={() => setShowSettings(true)}
              style={{
                background: "transparent", border: "1px solid #e2e8f0",
                borderRadius: "8px", padding: "0.45em 1em",
                cursor: "pointer", color: "#718096", fontSize: "0.85rem",
                display: "flex", alignItems: "center", gap: "0.4em",
              }}
            >
              ⚙ GDrive Einstellungen
            </button>
            <button
              disabled={selected.size === 0}
              onClick={proceedToMatch}
              style={{
                ...btnPrimary,
                opacity: selected.size === 0 ? 0.45 : 1,
                cursor:  selected.size === 0 ? "not-allowed" : "pointer",
              }}
            >
              Weiter ({selected.size}) →
            </button>
          </div>
        </div>

        {/* Filter pills */}
        {(() => {
          const counts = {
            offen:      orders.filter((o) => o.druckStatus !== "verschoben").length,
            verschoben: orders.filter((o) => o.druckStatus === "verschoben").length,
            alle:       orders.length,
          };
          return (
            <div style={{ display: "flex", gap: "0.5em", marginBottom: "1em" }}>
              {[
                { key: "offen",      label: "Offen" },
                { key: "verschoben", label: "Verschoben" },
                { key: "alle",       label: "Alle" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setStageFilter(key)}
                  style={{
                    padding: "0.35em 1em", borderRadius: "6px", fontSize: "0.85rem",
                    fontWeight: "600", cursor: "pointer",
                    border: `1px solid ${stageFilter === key ? "#553c9a" : "#cbd5e0"}`,
                    background: stageFilter === key ? "#553c9a" : "#fff",
                    color: stageFilter === key ? "#fff" : "#4a5568",
                  }}
                >
                  {label} ({counts[key]})
                </button>
              ))}
            </div>
          );
        })()}

        <div style={card}>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Suche nach Name, Bestellung oder Artikel…"
            style={{
              width: "100%", padding: "0.5em 0.75em", marginBottom: "1em",
              border: "1px solid #e2e8f0", borderRadius: "8px",
              fontSize: "0.9rem", boxSizing: "border-box",
            }}
          />
          {loading ? (
            <p style={{ color: "#718096" }}>Lade…</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>
                      <input
                        type="checkbox"
                        checked={filteredOrders.length > 0 &&
                                 filteredOrders.every((o) => selected.has(o.id))}
                        onChange={() => toggleAll(filteredOrders)}
                      />
                    </th>
                    <th style={th}>Bestellung</th>
                    <th style={th}>Käufer</th>
                    <th style={th}>Artikel</th>
                    <th style={th}>Lieferdatum</th>
                    <th style={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ ...td, color: "#718096",
                                               textAlign: "center", padding: "2em" }}>
                        Keine Bestellungen in Druck-Status gefunden.
                      </td>
                    </tr>
                  )}
                  {filteredOrders.map((o) => (
                    <tr
                      key={o.id}
                      onClick={() => toggleSelect(o.id)}
                      style={{
                        cursor: "pointer",
                        background: selected.has(o.id) ? "#f5f3ff" : "transparent",
                      }}
                    >
                      <td style={td}>
                        <input
                          type="checkbox"
                          checked={selected.has(o.id)}
                          onChange={() => toggleSelect(o.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={td}>{o.id}</td>
                      <td style={td}>{o.buyerName || "–"}</td>
                      <td style={{ ...td, maxWidth: 280 }}>
                        {o.items?.map((i, idx) => (
                          <div key={idx} style={{ whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis" }}>
                            {truncate(i.title, 55)}
                          </div>
                        )) || "–"}
                      </td>
                      <td style={td}>{formatDate(o.shipByDate)}</td>
                      <td style={td}>
                        {o.druckStatus === "verschoben" && (
                          <span style={{
                            display: "inline-block",
                            padding: "0.15em 0.65em", borderRadius: "999px",
                            fontSize: "0.75rem", fontWeight: "600",
                            background: "#d1fae5", color: "#065f46",
                          }}>
                            verschoben
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {filteredOrders.length > 3 && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              disabled={selected.size === 0}
              onClick={proceedToMatch}
              style={{
                ...btnPrimary,
                opacity: selected.size === 0 ? 0.45 : 1,
                cursor:  selected.size === 0 ? "not-allowed" : "pointer",
              }}
            >
              Weiter ({selected.size}) →
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Step 2: matching / match review ────────────────────────────────────────

  if (step === "match") {
    const isMatching  = !jobData || jobData.status === "matching";
    const isMatched   = jobData?.status === "matched";
    const isConfirmed = jobData?.status === "confirmed";
    const isDone      = jobData?.status === "done";
    const isError     = jobData?.status === "error";

    // ── Step 3: done ──────────────────────────────────────────────────────────
    if (isDone) {
      const moved  = jobData.movedOrders  || [];
      const failed = jobData.failedOrders || [];
      return (
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <h2 style={{ fontWeight: "700", fontSize: "1.3rem", color: "#1a202c",
                       marginBottom: "1.5em" }}>
            Produktion — Ergebnis
          </h2>
          <div style={card}>
            {moved.length > 0 && (
              <div style={{ marginBottom: "1em" }}>
                <strong style={{ fontSize: "0.85rem", color: "#065f46" }}>
                  Verschoben ({moved.length}):
                </strong>
                {moved.map((oid) => {
                  const o = orders.find((x) => x.id === oid);
                  return (
                    <div key={oid} style={{ fontSize: "0.85rem", padding: "0.3em 0",
                                           color: "#2d3748" }}>
                      ✓ {oid} — {o?.buyerName || ""}
                    </div>
                  );
                })}
              </div>
            )}
            {failed.length > 0 && (
              <div>
                <strong style={{ fontSize: "0.85rem", color: "#991b1b" }}>
                  Fehler ({failed.length}):
                </strong>
                {failed.map((oid) => {
                  const o = orders.find((x) => x.id === oid);
                  return (
                    <div key={oid} style={{ fontSize: "0.85rem", padding: "0.3em 0",
                                           color: "#c53030" }}>
                      ✗ {oid} — {o?.buyerName || ""}
                    </div>
                  );
                })}
              </div>
            )}
            {moved.length === 0 && failed.length === 0 && (
              <p style={{ color: "#718096", fontSize: "0.85rem" }}>
                Keine Dateien wurden verschoben.
              </p>
            )}
          </div>
          <button onClick={finishDone} style={btnPrimary}>Fertig</button>
        </div>
      );
    }

    const matches     = jobData?.matches || {};
    const canMove     = isMatched && !confirming &&
                        allToMoveResolved(matches, selections, toMove);
    const moveCount   = toMove.size;

    return (
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: "1.5em" }}>
          <h2 style={{ fontWeight: "700", fontSize: "1.3rem", color: "#1a202c", margin: 0 }}>
            Produktion — Datei-Zuordnung
          </h2>
          <div style={{ display: "flex", gap: "0.75em" }}>
            {isMatched && !confirming && (
              <button onClick={cancelJob} style={btnSecondary}>← Zurück</button>
            )}
            <button
              disabled={!canMove}
              onClick={confirmMove}
              style={{
                ...btnPrimary,
                opacity: !canMove ? 0.45 : 1,
                cursor:  !canMove ? "not-allowed" : "pointer",
              }}
            >
              {confirming
                ? "Verschiebe…"
                : `Dateien verschieben (${moveCount}) →`}
            </button>
          </div>
        </div>

        {/* Spinners */}
        {(isMatching || isConfirmed) && (
          <div style={{ ...card, textAlign: "center", padding: "3em" }}>
            <div style={{ fontSize: "1.4em", marginBottom: "0.5em" }}>⏳</div>
            <p style={{ color: "#4a5568", fontWeight: "500", margin: 0 }}>
              {isMatching
                ? "Dateien werden gesucht… (bis zu 10 Sekunden)"
                : "Dateien werden verschoben…"}
            </p>
            <p style={{ color: "#a0aec0", fontSize: "0.82rem", marginTop: "0.5em" }}>
              {isMatching
                ? "Der Scraper sucht im GDrive-Ordner nach passenden Dateien."
                : "Der Scraper verschiebt die ausgewählten Dateien auf Google Drive."}
            </p>
          </div>
        )}

        {isError && (
          <div style={{ ...card, background: "#fff5f5", border: "1px solid #feb2b2" }}>
            <strong style={{ color: "#c53030" }}>Fehler: </strong>
            <span style={{ color: "#c53030", fontSize: "0.875rem" }}>
              {jobData.errorMessage || "Unbekannter Fehler"}
            </span>
            <div style={{ marginTop: "1em" }}>
              <button onClick={cancelJob} style={btnSecondary}>← Zurück</button>
            </div>
          </div>
        )}

        {/* Match table */}
        {isMatched && (
          <div style={card}>
            {/* Info banner */}
            <div style={{
              background: "#eff6ff", border: "1px solid #bfdbfe",
              borderRadius: "8px", padding: "0.75em 1em",
              fontSize: "0.83rem", color: "#1e40af", marginBottom: "1em",
            }}>
              Haken = diese Bestellung wird verschoben. Standardmäßig sind nur
              Bestellungen mit eindeutiger Datei-Zuordnung aktiviert. Konflikte können
              per Dropdown aufgelöst werden, dann den Haken setzen.
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>
                      {/* Select all / none */}
                      <input
                        type="checkbox"
                        title="Alle auswählen"
                        checked={moveCount === selectedOrders.length && moveCount > 0}
                        onChange={() => {
                          const allIds = selectedOrders.map((o) => o.id);
                          const allOn  = allIds.every((id) => toMove.has(id));
                          setToMove(allOn ? new Set() : new Set(allIds));
                        }}
                      />
                    </th>
                    <th style={th}>Bestellung</th>
                    <th style={th}>Käufer</th>
                    <th style={th}>Artikel</th>
                    <th style={th}>Datei</th>
                    <th style={th}>Konfidenz</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrders.map((order) => {
                    const items      = matches[order.id] || [];
                    const isChecked  = toMove.has(order.id);
                    const resolved   = orderResolved(order.id, items, selections);
                    const hasConflict = items.some(
                      (m) => m.candidates?.length !== 1
                    );

                    return items.map((m, idx) => {
                      const key      = `${order.id}_${idx}`;
                      const curSels  = Array.isArray(selections[key])
                        ? selections[key]
                        : (m.selectedId ? [m.selectedId] : []);
                      const cands    = m.candidates || [];
                      const selCand  = cands.find((c) => curSels.includes(c.fileId));

                      return (
                        <tr
                          key={key}
                          style={{
                            background: isChecked ? "#f5f3ff" : "transparent",
                            opacity:    isChecked ? 1 : 0.6,
                          }}
                        >
                          {/* Checkbox: only on first item row */}
                          {idx === 0 ? (
                            <td style={td} rowSpan={items.length}
                                onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setToMove((prev) => {
                                    const next = new Set(prev);
                                    next.has(order.id)
                                      ? next.delete(order.id)
                                      : next.add(order.id);
                                    return next;
                                  });
                                }}
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                          ) : null}

                          {idx === 0 && (
                            <>
                              <td style={td} rowSpan={items.length}>
                                {order.id}
                                {hasConflict && !resolved && (
                                  <span style={{ color: "#d97706", marginLeft: "0.4em",
                                                 fontSize: "0.8em" }} title="Konflikt">
                                    ⚠
                                  </span>
                                )}
                              </td>
                              <td style={td} rowSpan={items.length}>
                                {order.buyerName || "–"}
                              </td>
                            </>
                          )}

                          <td style={{ ...td, fontSize: "0.78rem", color: "#4a5568",
                                       maxWidth: 200, overflow: "hidden",
                                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {truncate(m.itemTitle, 45) || "–"}
                          </td>

                          <td style={td}>
                            {cands.length === 0 && (
                              <span style={{ color: "#c53030", fontWeight: "600" }}>
                                ⚠ Nicht gefunden
                              </span>
                            )}
                            {cands.length === 1 && (
                              <span style={{ color: "#065f46" }}>
                                ✓ {cands[0].name}
                              </span>
                            )}
                            {cands.length >= 2 && (
                              <div style={{ display: "flex", flexDirection: "column",
                                            gap: "0.35em" }}>
                                {cands.map((c) => {
                                  const checked = curSels.includes(c.fileId);
                                  return (
                                    <label key={c.fileId}
                                           style={{ display: "flex", alignItems: "center",
                                                    gap: "0.4em", fontSize: "0.82rem",
                                                    cursor: "pointer" }}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          setSelections((prev) => {
                                            const cur = Array.isArray(prev[key]) ? prev[key] : [];
                                            const next = e.target.checked
                                              ? [...cur, c.fileId]
                                              : cur.filter((id) => id !== c.fileId);
                                            return { ...prev, [key]: next };
                                          });
                                          if (e.target.checked) {
                                            setToMove((prev) => {
                                              const next = new Set(prev);
                                              next.add(order.id);
                                              return next;
                                            });
                                          }
                                        }}
                                        style={{ accentColor: "#553c9a", cursor: "pointer" }}
                                      />
                                      <span style={confBadge(c.confidence)}>
                                        {c.confidence === "high" ? "Hoch"
                                         : c.confidence === "medium" ? "Mittel" : "Niedrig"}
                                      </span>
                                      {c.name}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </td>

                          <td style={td}>
                            {cands.length <= 1 && selCand && (
                              <span style={confBadge(selCand.confidence)}>
                                {selCand.confidence === "high"   ? "Hoch"
                               : selCand.confidence === "medium" ? "Mittel"
                               :                                    "Niedrig"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bottom action bar */}
        {isMatched && (
          <div style={{ display: "flex", justifyContent: "flex-end",
                        gap: "0.75em", marginTop: "0.5em" }}>
            <button onClick={cancelJob} style={btnSecondary}>← Zurück</button>
            <button
              disabled={!canMove}
              onClick={confirmMove}
              style={{
                ...btnPrimary,
                opacity: !canMove ? 0.45 : 1,
                cursor:  !canMove ? "not-allowed" : "pointer",
              }}
            >
              {confirming
                ? "Verschiebe…"
                : `Dateien verschieben (${moveCount}) →`}
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
